
import { motion } from "framer-motion";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useToast } from "@/components/ui/use-toast";
import QuizHeader from "@/components/beginner-quiz/QuizHeader";
import QuizProgress from "@/components/beginner-quiz/QuizProgress";
import QuizQuestion from "@/components/beginner-quiz/QuizQuestion";
import QuizResults from "@/components/beginner-quiz/QuizResults";
import QuizActions from "@/components/beginner-quiz/QuizActions";
import QuizIntro from "@/components/beginner-quiz/QuizIntro";
import { quizQuestions } from "@/components/beginner-quiz/quizData";
import LoadingState from "@/components/get-started/LoadingState";

const CryptoTradingQuiz = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [showIntro, setShowIntro] = useState(true);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [score, setScore] = useState(0);
  const [userAnswers, setUserAnswers] = useState<{[key: number]: string}>({});
  const [correctAnswers, setCorrectAnswers] = useState<{[key: number]: boolean}>({});
  const [quizComplete, setQuizComplete] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleStartQuiz = () => {
    setShowIntro(false);
  };

  const handleSkipQuiz = () => {
    navigate("/signup");
  };

  const handleNextQuestion = (selectedOption: string, correct: boolean) => {
    setUserAnswers(prev => ({
      ...prev,
      [quizQuestions[currentQuestionIndex].id]: selectedOption
    }));
    setCorrectAnswers(prev => ({
      ...prev,
      [quizQuestions[currentQuestionIndex].id]: correct
    }));
    if (correct) {
      setScore(prev => prev + 1);
    }
    if (currentQuestionIndex < quizQuestions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    } else {
      setQuizComplete(true);
    }
  };

  const handleContinue = () => {
    setLoading(true);
    let riskLevel = '';
    if (correctAnswers[4]) {
      riskLevel = 'low';
    } else {
      riskLevel = 'medium';
    }
    let investmentAmount = '';
    if (score >= 4) {
      investmentAmount = '1000';
    } else if (score >= 2) {
      investmentAmount = '500';
    } else {
      investmentAmount = '100';
    }
    let exchange = '';
    const copyTradingAnswerCorrect = correctAnswers[1];
    const executionAnswerCorrect = correctAnswers[5];
    if (copyTradingAnswerCorrect && executionAnswerCorrect) {
      exchange = 'bybit';
    } else {
      exchange = 'okx';
    }
    setTimeout(() => {
      const riskLevelNumber = riskLevel === 'low' ? '1' : riskLevel === 'medium' ? '2' : '3';
      const exchangeMap: { [key: string]: string } = {
        'okx': 'OKX',
        'bybit': 'Bybit',
        'bitget': 'Bitget'
      };
      const formattedExchange = exchangeMap[exchange] || '';
      console.log("Navigating with filters from quiz:", {
        minInvestment: investmentAmount,
        riskLevel: riskLevelNumber ? [riskLevelNumber] : [],
        exchange: formattedExchange ? [formattedExchange] : [],
      });
      toast({
        title: "Quiz Complete!",
        description: "We've analyzed your answers and found strategies that match your knowledge level.",
      });
      navigate("/signup", {
        state: {
          filters: {
            minInvestment: investmentAmount,
            riskLevel: riskLevelNumber ? [riskLevelNumber] : [],
            exchange: formattedExchange ? [formattedExchange] : [],
            quizScore: score,
            totalQuestions: quizQuestions.length
          },
        },
      });
    }, 1500);
  };

  return (
    <div className="min-h-screen bg-[#F5F7FB]">
      <Header />
      <main className="container mx-auto px-4 py-12 md:py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-2xl mx-auto"
        >
          <QuizHeader />
          <div className="bg-white rounded-xl shadow-md p-6 md:p-8">
            {loading ? (
              <div className="py-8">
                <LoadingState />
              </div>
            ) : (
              <>
                {showIntro ? (
                  <QuizIntro 
                    onStart={handleStartQuiz} 
                    onSkip={handleSkipQuiz} 
                  />
                ) : (
                  !quizComplete ? (
                    <>
                      <QuizProgress 
                        currentQuestion={currentQuestionIndex + 1} 
                        totalQuestions={quizQuestions.length} 
                      />
                      <QuizQuestion
                        key={quizQuestions[currentQuestionIndex].id}
                        questionData={quizQuestions[currentQuestionIndex]}
                        onNext={handleNextQuestion}
                      />
                    </>
                  ) : (
                    <QuizResults 
                      score={score} 
                      totalQuestions={quizQuestions.length}
                      onContinue={handleContinue}
                    />
                  )
                )}
                
                {!quizComplete && !showIntro && (
                  <QuizActions 
                    loading={loading} 
                    onSkip={handleSkipQuiz}
                  />
                )}
              </>
            )}
          </div>
        </motion.div>
      </main>
      <Footer />
    </div>
  );
};

export default CryptoTradingQuiz;
